import * as THREE from "three";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/** Figma Group 644 (node 1637:38344) — 1492×775（コラージュ座標は 695 基準） */
const DESIGN_PC = { width: 1492, height: 775 };
const DESIGN_PC_COLLAGE_H = 695;
/** Figma node 1880:5808 — SP */
const DESIGN_SP = { width: 375, height: 670 };
const MV_SP_MQ = "(max-width: 1024px)";

function splitTextIntoSpans(element) {
  const text = element.textContent.trim();
  element.innerHTML = "";
  const chars = [];
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const span = document.createElement("span");
    span.textContent = char;
    if (char === " ") {
      span.innerHTML = "&nbsp;";
    }
    span.style.opacity = 0;
    span.style.display = "inline-block";
    element.appendChild(span);
    chars.push(span);
  }
  return chars;
}

function getDesignFromRoot(root, fallback) {
  if (!root) return fallback;

  const style = getComputedStyle(root);
  const width = parseFloat(style.getPropertyValue("--hm-mv-design-w"));
  const height = parseFloat(style.getPropertyValue("--hm-mv-design-h"));

  if (width > 0 && height > 0) {
    return { width, height };
  }

  return fallback;
}

function scaleLayoutY(item, scaleY) {
  if (scaleY === 1) return item;

  return {
    ...item,
    y: item.y * scaleY,
    h: item.h * scaleY,
  };
}

function getMvLayout(root) {
  const isSp = window.matchMedia(MV_SP_MQ).matches;
  const fallback = isSp ? DESIGN_SP : DESIGN_PC;
  const design = getDesignFromRoot(root, fallback);

  const scaleY = design.height / DESIGN_PC_COLLAGE_H;
  const pairs = isSp
    ? PAIRS_SP
    : scaleY === 1
    ? PAIRS
    : PAIRS.map((pair) => ({
        ...pair,
        items:
          pair.id === "main"
            ? pair.items
            : pair.items.map((item) => scaleLayoutY(item, scaleY)),
      }));

  return { design, pairs, isSpStatic: false, isSp };
}
const Z_FRONT = 40;
const Z_BACK = -40;
const BLUR_PX = 20;
/** Nealle 風 — 背面 / 前面レイヤーの右→左フロー周期（秒） */
const FLOW_TOP_BACK_DURATION = 90;
const FLOW_BOTTOM_BACK_DURATION = 80;
const FLOW_TOP_FRONT_DURATION = 46;
const FLOW_BOTTOM_FRONT_DURATION = 46;
const MAIN_FADE_DURATION = 0.8;
const COLOR_SHIFT_DURATION = 0.6;
/** 初回表示: 最後のコラージュがフェードイン完了する時刻（秒） */
const INTRO_LAST_PAIR_START = 0.2 + 0.4 * 3;
const INTRO_PAIR_FADE_DURATION = 3.2;
/** コラージュ表示完了後、右→左フローを始めるまでの静止（秒） */
const FLOW_HOLD_AFTER_INTRO = 1.5;
/** 第一陣（青）と第二陣（オレンジ）の間隔（px） */
const FLOW_STRIP_GAP = 180;
/** フローアニメーション開始遅延（秒） */
const MAIN_LOOP_DELAY = 8.5;

/**
 * GSAP easing 設定（GSAP ease 名 / CustomEase 文字列）
 * @see https://gsap.com/docs/v3/Eases
 */
export const DEFAULT_EASING = {
  /** main 01/02 のクロスフェード */
  mainFade: "power2.inOut",
  /** 背景グラデーションの色シフト */
  colorShift: "linear",
  /** 初回表示: main フェードイン */
  introMain: "power3.out",
  /** 初回表示: その他ペアのフェードイン */
  introPair: "power3.out",
};

const EASING_DATA_ATTRS = {
  mainFade: "hmMvEaseMainFade",
  colorShift: "hmMvEaseColorShift",
  introMain: "hmMvEaseIntroMain",
  introPair: "hmMvEaseIntroPair",
};

/** デフォルト + data属性 + initHmMv({ easing }) をマージ */
function resolveEasing(root, overrides = {}) {
  const easing = { ...DEFAULT_EASING };

  if (root?.dataset) {
    for (const [key, attr] of Object.entries(EASING_DATA_ATTRS)) {
      const value = root.dataset[attr];
      if (value) easing[key] = value;
    }
  }

  return { ...easing, ...overrides };
}

/** GSAP が inline 設定する表示用カラー変数 */
const HM_MV_COLOR_VARS = [
  "--hm-mv-grad-1-from",
  "--hm-mv-grad-1-to",
  "--hm-mv-grad-2-from",
  "--hm-mv-grad-2-to",
  "--hm-mv-poly-deep",
  "--hm-mv-poly-mid",
  "--hm-mv-poly-sky",
  "--hm-mv-poly-teal",
  "--hm-mv-bg-from",
  "--hm-mv-bg-to",
  "--hm-mv-lead-color",
];

/** inline を外して CSS @keyframes に制御を戻す */
function releaseHmMvColorControl(root) {
  gsap.set(root, { clearProps: HM_MV_COLOR_VARS.join(",") });
  root.classList.remove("is-warm");
}

/** CSS 変数から青/オレンジのパレットを取得（色は _hm-mv.css で指定） */
function getHmMvColorPalette(root) {
  const style = getComputedStyle(root);

  return {
    blue: {
      "--hm-mv-grad-1-from": style.getPropertyValue("--hm-mv-blue-grad-1-from").trim(),
      "--hm-mv-grad-1-to": style.getPropertyValue("--hm-mv-blue-grad-1-to").trim(),
      "--hm-mv-grad-2-from": style.getPropertyValue("--hm-mv-blue-grad-2-from").trim(),
      "--hm-mv-grad-2-to": style.getPropertyValue("--hm-mv-blue-grad-2-to").trim(),
      "--hm-mv-poly-deep": style.getPropertyValue("--hm-mv-blue-poly-deep").trim(),
      "--hm-mv-poly-mid": style.getPropertyValue("--hm-mv-blue-poly-mid").trim(),
      "--hm-mv-poly-sky": style.getPropertyValue("--hm-mv-blue-poly-sky").trim(),
      "--hm-mv-poly-teal": style.getPropertyValue("--hm-mv-blue-poly-teal").trim(),
      "--hm-mv-bg-from": style.getPropertyValue("--hm-mv-blue-bg-from").trim(),
      "--hm-mv-bg-to": style.getPropertyValue("--hm-mv-blue-bg-to").trim(),
      "--hm-mv-lead-color": style.getPropertyValue("--hm-mv-blue-lead").trim(),
    },
    orange: {
      "--hm-mv-grad-1-from": style.getPropertyValue("--hm-mv-orange-grad-1-from").trim(),
      "--hm-mv-grad-1-to": style.getPropertyValue("--hm-mv-orange-grad-1-to").trim(),
      "--hm-mv-grad-2-from": style.getPropertyValue("--hm-mv-orange-grad-2-from").trim(),
      "--hm-mv-grad-2-to": style.getPropertyValue("--hm-mv-orange-grad-2-to").trim(),
      "--hm-mv-poly-deep": style.getPropertyValue("--hm-mv-orange-poly-deep").trim(),
      "--hm-mv-poly-mid": style.getPropertyValue("--hm-mv-orange-poly-mid").trim(),
      "--hm-mv-poly-sky": style.getPropertyValue("--hm-mv-orange-poly-sky").trim(),
      "--hm-mv-poly-teal": style.getPropertyValue("--hm-mv-orange-poly-teal").trim(),
      "--hm-mv-bg-from": style.getPropertyValue("--hm-mv-orange-bg-from").trim(),
      "--hm-mv-bg-to": style.getPropertyValue("--hm-mv-orange-bg-to").trim(),
      "--hm-mv-lead-color": style.getPropertyValue("--hm-mv-orange-lead").trim(),
    },
  };
}



function createHmMvColorShift(root, easing) {
  const palette = getHmMvColorPalette(root);
  let colorTween = null;

  const shiftTo = (targetPalette, { onStart, onComplete } = {}) => {
    colorTween?.kill();
    colorTween = gsap.to(root, {
      duration: COLOR_SHIFT_DURATION,
      ease: easing.colorShift,
      ...targetPalette,
      onStart,
      onComplete,
    });
  };

  return {
    toOrange: () => {
      root.classList.add("is-warm");
      shiftTo(palette.orange);
    },
    toBlue: () => {
      shiftTo(palette.blue, {
        onComplete: () => {
          // inline が残ると @keyframes が効かないため、先に解除してから is-warm を外す
          releaseHmMvColorControl(root);
        },
      });
    },
    resetBlue: () => {
      colorTween?.kill();
      releaseHmMvColorControl(root);
    },
  };
}

const PAIRS = [
  {
    id: "left-top",
    delay: 0,
    items: [
      {
        file: "img-mv-left-top01.webp",
        x: 128.22,
        y: 54,
        w: 307.43,
        h: 189.19,
      },
      {
        file: "img-mv-left-top02.webp",
        x: 0,
        y: 93.86,
        w: 155.33,
        h: 95.59,
      },
    ],
  },
  {
    id: "center-top",
    delay: 0.6,
    items: [
      {
        file: "img-mv-center-top01.webp",
        x: 565.22,
        y: 16,
        w: 459.19,
        h: 257,
      },
      {
        file: "img-mv-center-top02.webp",
        x: 951.22,
        y: 214,
        w: 123,
        h: 76,
      },
    ],
  },
  {
    id: "right-top",
    delay: 1.2,
    items: [
      {
        file: "img-mv-right-top01.webp",
        x: 1135.22,
        y: 58,
        w: 300,
        h: 180.36,
      },
      {
        file: "img-mv-right-top02.webp",
        x: 1105.22,
        y: 0,
        w: 109,
        h: 65.4,
      },
    ],
  },
  {
    id: "right-bottom",
    delay: 1.8,
    items: [
      {
        file: "img-mv-right-bottom01.webp",
        x: 960.22,
        y: 313,
        w: 531.38,
        h: 327,
      },
      {
        file: "img-mv-right-bottom02.webp",
        x: 1186.22,
        y: 265,
        w: 178,
        h: 109.4,
      },
    ],
  },
  {
    id: "main",
    delay: 1.2,
    items: [
      {
        file: "img-mv-main01.webp",
        x: 552,
        y: 280,
        w: 387,
        h: 442,
      },
      {
        file: "img-mv-main02.webp",
        x: 552,
        y: 280,
        w: 290,
        h: 342,
      },
    ],
  },
];

const PAIRS_SP = [
  {
    id: "center-top",
    delay: 1.0,
    items: [
      { file: "img-mv-center-top01.webp", fileNext: "img-mv-right-bottom01-next.webp", x: -112, y: 243, w: 251, h: 172 }, // Top Left Front
      { file: "img-mv-left-top02.webp", fileNext: "img-mv-right-top02-next.webp", x: 65, y: 349, w: 112, h: 76 }, // Top Left Back
    ],
  },
  {
    id: "left-top",
    delay: 0.8,
    items: [
      { file: "img-mv-right-bottom01.webp", fileNext: "img-mv-right-top01-next.webp", x: 61, y: 429, w: 290, h: 198 }, // Bottom Left Front
      { file: "img-mv-left-top02.webp", fileNext: "img-mv-left-top02.webp", x: -10000, y: 0, w: 1, h: 1 }, // Dummy Back
    ],
  },
  {
    id: "right-bottom",
    delay: 1.2,
    items: [
      { file: "img-mv-left-top01-next.webp", fileNext: "img-mv-left-top01.webp", x: 380, y: 453, w: 206, h: 141 }, // Bottom Right Front
      { file: "img-mv-right-bottom02-next.webp", fileNext: "img-mv-right-bottom02.webp", x: 256, y: 217, w: 135, h: 92 }, // Top Right Back
    ],
  },
  {
    id: "right-top",
    delay: 0.9,
    items: [
      { file: "img-mv-left-top02.webp", fileNext: "img-mv-left-top02.webp", x: -10000, y: 0, w: 1, h: 1 }, // Dummy Front
      { file: "img-mv-left-top02.webp", fileNext: "img-mv-left-top02.webp", x: -10000, y: 0, w: 1, h: 1 }, // Dummy Back
    ],
  },
  {
    id: "main",
    delay: 1.2,
    items: [
      { file: "img-mv-main01.webp", x: 189, y: 221, w: 195, h: 216 }, // Top Right Front
      { file: "img-mv-main02.webp", x: 189, y: 180, w: 195, h: 216 }, // Top Right Front Next
    ],
  },
];

const SP_STATIC_IMAGE = "img-mv-sp.webp";

/** タイトル SVG + 英語リード（HTML .hm-mv__copy と同じ配置） */
const HERO_COPY = {
  title: { file: "img-mv-title.svg", x: 130, y: 360, w: 397, h: 193 },
  lead: { x: 130, y: 585, w: 397, h: 63 },
  lines: [
    "People + Skill + Thermal",
    "Creating a comfortable tomorrow.",
    "Connecting to the future.",
  ],
};
/** 背景色切替をテキスト完全表示より早める余白（px） */
const COLOR_SWITCH_EARLY_PX = 140;
const HERO_RENDER_ORDER = 25;
const LEAD_FONT =
  '500 16px "Plus Jakarta Sans", "Helvetica Neue", Arial, sans-serif';
const LEAD_LINE_HEIGHT = 16 * 1.3125;

/** SP: 単一画像をキャンバス全面に表示（Figma node 1880:5808） */
function getPairsSp(design) {
  return [
    {
      id: "sp-static",
      delay: 0,
      static: true,
      items: [
        {
          file: SP_STATIC_IMAGE,
          x: 0,
          y: 0,
          w: design.width,
          h: design.height,
        },
      ],
    },
  ];
}

const textureLoader = new THREE.TextureLoader();

/** img-mv-foo01.webp → img-mv-foo01-next.webp */
function getNextTextureFile(file) {
  return file.replace(/\.webp$/i, "-next.webp");
}

const TEXTURE_FALLBACK_EXTS = [".webp", ".jpg", ".jpeg", ".png"];

function getTextureCandidates(file) {
  const ext = /\.[^.]+$/.exec(file)?.[0]?.toLowerCase() ?? "";
  const base = ext ? file.slice(0, -ext.length) : file;

  return TEXTURE_FALLBACK_EXTS.map((candidateExt) => `${base}${candidateExt}`);
}

function loadTexture(basePath, file) {
  const candidates = getTextureCandidates(file);

  const tryLoad = (index) =>
    new Promise((resolve, reject) => {
      if (index >= candidates.length) {
        reject(new Error(`[hm-mv] Texture not found: ${file}`));
        return;
      }

      textureLoader.load(
        `${basePath}${candidates[index]}`,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          resolve(texture);
        },
        undefined,
        () => {
          tryLoad(index + 1).then(resolve).catch(reject);
        },
      );
    });

  return tryLoad(0);
}

function loadSvgImage(basePath, file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`[hm-mv] SVG not found: ${file}`));
    img.src = `${basePath}${file}`;
  });
}

function createHeroImageTexture(image, layout) {
  const canvas = document.createElement("canvas");
  const dpr = 2;
  canvas.width = Math.ceil(layout.w * dpr);
  canvas.height = Math.ceil(layout.h * dpr);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.scale(dpr, dpr);
  const scale = Math.min(layout.w / image.width, layout.h / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  ctx.drawImage(image, (layout.w - width) / 2, (layout.h - height) / 2, width, height);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function createLeadTexture(lines, color, layout) {
  const canvas = document.createElement("canvas");
  const dpr = 2;
  canvas.width = Math.ceil(layout.w * dpr);
  canvas.height = Math.ceil(layout.h * dpr);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, layout.w, layout.h);
  ctx.fillStyle = color;
  ctx.font = LEAD_FONT;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  lines.forEach((line, index) => {
    ctx.fillText(line, 22, index * LEAD_LINE_HEIGHT);
  });

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function showHmMvFallback(root, stage) {
  stage.classList.add("is-fallback");
  root?.classList.add("is-bg-in", "is-visual-in", "is-overlay-in");
}

function createBlurredTexture(texture, blurPx = BLUR_PX) {
  const image = texture.image;
  if (!image?.width) return texture;

  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return texture;

  ctx.filter = `blur(${blurPx}px)`;
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  const blurred = new THREE.CanvasTexture(canvas);
  blurred.colorSpace = THREE.SRGBColorSpace;
  blurred.flipY = texture.flipY;
  return blurred;
}

/** レイヤー群を右→左へシームレスループ（Nealle slider-home-hero 準拠） */
function getFlowFromX() {
  return 0;
}

function getFlowStripPitch(designWidth) {
  return designWidth + FLOW_STRIP_GAP;
}

function getFlowCycleWidth(designWidth) {
  return getFlowStripPitch(designWidth) * 2;
}

function setFlowGroupProgress(group, designWidth, progress) {
  const cycleWidth = getFlowCycleWidth(designWidth);
  group.position.x = getFlowFromX() - progress * cycleWidth;
}

function createFlowController(groups) {
  return {
    groups,
    startedAt: null,
    lastTime: null,
    internalElapsed: 0,
    isPaused: false,
    pauseTimer: 0,
    hasPausedFor05: false, // Prevents pausing on the very first blue image (target1)
    hasPausedFor0: false,
    activeColor: "blue",
  };
}

function startFlowController(controller) {
  const now = performance.now();
  controller.startedAt = now;
  controller.lastTime = now;
}

/** render と同期して位置を更新（GSAP onUpdate とのズレで飛ぶのを防ぐ） */
function updateFlowController(controller, designWidth, colorShift) {
  const fromX = getFlowFromX();
  const isIndex3 = (window.location.pathname.includes("index3") || window.location.pathname === "/" || window.location.pathname.endsWith("/index.php") || window.location.pathname.endsWith("/html/"));
  const isIndex4 = window.location.pathname.includes("index4");

  if (!controller.startedAt) {
    for (const { group } of controller.groups) {
      setFlowGroupProgress(group, designWidth, 0);
    }
    return;
  }

  const now = performance.now();
  let delta = (now - controller.lastTime) / 1000;
  if (delta > 0.1) delta = 1 / 60; // Fallback to prevent huge skips if tab was inactive
  controller.lastTime = now;

  if (controller.isPaused) {
    controller.pauseTimer -= delta;
    if (controller.pauseTimer <= 0) {
      controller.isPaused = false;
    } else {
      return; // Skip updating position while paused
    }
  }

  controller.internalElapsed += delta;

  if (isIndex3 || isIndex4) {
    // main01 and main02 are in the BOTTOM flow groups, so their duration is 46s!
    const baseDuration = FLOW_BOTTOM_FRONT_DURATION;
    const progress = (controller.internalElapsed % baseDuration) / baseDuration;

    const cycleWidth = (designWidth + 180) * 2;
    // We want the left edge of main01/02 (layout x=552) to touch the right edge of title (x=527).
    // This requires the flow group to move left by exactly 25px.
    // Since group.position.x = -progress * cycleWidth, we want progress = 25 / cycleWidth.
    const pauseOffset = 25 / cycleWidth;
    const target1 = pauseOffset;
    const target2 = 0.5 + pauseOffset;

    const isSp = window.matchMedia("(max-width: 768px)").matches;

    // Check pause for main02 (orange clone, around 0.5)
    if (progress >= target2 && progress < target2 + 0.05 && !controller.hasPausedFor05) {
      controller.isPaused = true;
      controller.pauseTimer = 4.0;
      controller.hasPausedFor05 = true;
      controller.hasPausedFor0 = false;
      
      if (isSp && colorShift) {
        controller.activeColor = "orange";
        colorShift.toOrange();
      }
    }
    // Check pause for main01 (blue original, around 0.0)
    else if (progress >= target1 && progress < target1 + 0.05 && controller.hasPausedFor05 && !controller.hasPausedFor0) {
      controller.isPaused = true;
      controller.pauseTimer = 4.0;
      controller.hasPausedFor0 = true;
      controller.hasPausedFor05 = false;
      
      if (isSp && colorShift) {
        controller.activeColor = "blue";
        colorShift.toBlue();
      }
    }

    // Color shift based on img-mv-left-top01 passing the center (PC only)
    if (!isSp) {
      const cx = 282; // center x of left-top01 on PC
      const trackingDuration = FLOW_TOP_FRONT_DURATION;
      const trackingProgress = (controller.internalElapsed % trackingDuration) / trackingDuration;
      
      let xBlue = (cx - trackingProgress * cycleWidth) % cycleWidth;
      if (xBlue < 0) xBlue += cycleWidth;
      
      const centerScreen = designWidth / 2;
      let nextColor = "blue";
      if (xBlue > centerScreen && xBlue <= centerScreen + cycleWidth / 2) {
        nextColor = "orange";
      }
      
      if (controller.activeColor !== nextColor) {
        controller.activeColor = nextColor;
        if (colorShift) {
          if (nextColor === "orange") colorShift.toOrange();
          else colorShift.toBlue();
        }
      }
    }
  }

  for (const { group, duration } of controller.groups) {
    const groupProgress = (controller.internalElapsed % duration) / duration;
    setFlowGroupProgress(group, designWidth, groupProgress);
  }
}

/** 前面フローの位置から、表示中セグメント（blue = 通常 / orange = -next 群）を判定 */
function getHeroCopyBounds(stripOffset) {
  return {
    left: stripOffset + HERO_COPY.title.x,
    right: stripOffset + HERO_COPY.title.x + HERO_COPY.title.w,
    top: HERO_COPY.title.y,
    bottom: HERO_COPY.lead.y + HERO_COPY.lead.h,
  };
}

function isHeroCopyVisibleForColorSwitch(groupX, bounds, view, designHeight) {
  const left = groupX + bounds.left;
  const right = groupX + bounds.right;
  const early = COLOR_SWITCH_EARLY_PX;

  return (
    left >= view.left - early &&
    right <= view.right + early &&
    bounds.top >= 0 &&
    bounds.bottom <= designHeight
  );
}

function isStripHeroCopyVisible(group, designWidth, strip, view, designHeight) {
  const pitch = getFlowStripPitch(designWidth);
  const offsets = strip === "orange" ? [-pitch, pitch, pitch * 3] : [0, pitch * 2];

  return offsets.some((offset) =>
    isHeroCopyVisibleForColorSwitch(
      group.position.x,
      getHeroCopyBounds(offset),
      view,
      designHeight,
    ),
  );
}

function getFlowSegment(group, designWidth, designHeight, currentSegment) {
  const view = {
    left: 0,
    right: designWidth,
  };
  const orangeVisible = isStripHeroCopyVisible(
    group,
    designWidth,
    "orange",
    view,
    designHeight,
  );
  const blueVisible = isStripHeroCopyVisible(
    group,
    designWidth,
    "blue",
    view,
    designHeight,
  );

  if (currentSegment === "blue" && orangeVisible) return "orange";
  if (currentSegment === "orange" && blueVisible) return "blue";
  return currentSegment;
}

/** シームレスループ用に x 方向へ複製（見た目のループ用・オリジナルと opacity 同期） */
function duplicateFlowMesh(mesh, offsetX) {
  const clone = mesh.clone(true);
  clone.position.x += offsetX;
  if (mesh.material) clone.material = mesh.material;
  clone.frustumCulled = false;
  clone.userData.flowSource = mesh;
  return clone;
}

/** blue（-nextなし）→ orange（-nextあり）→ blue … の交互ストリップを構築 */
function populateAlternatingFlowGroup(
  group,
  blueMeshes,
  orangeMeshes,
  designWidth,
  meshesToDispose,
) {
  const pitch = getFlowStripPitch(designWidth);

  for (const mesh of blueMeshes) {
    group.add(mesh);
  }

  const orangePlaced = orangeMeshes.map((mesh) => {
    const placed = duplicateFlowMesh(mesh, pitch);
    group.add(placed);
    meshesToDispose.push(placed);
    return placed;
  });

  // 初期表示: 左に第二陣の後方（-pitch）、中央に第一陣、右に第二陣の先頭（+pitch）
  orangeMeshes.forEach((mesh, index) => {
    const tail = duplicateFlowMesh(mesh, -pitch);
    tail.userData.flowSource = orangePlaced[index];
    group.add(tail);
    meshesToDispose.push(tail);
  });

  for (const mesh of blueMeshes) {
    const clone = duplicateFlowMesh(mesh, pitch * 2);
    clone.userData.flowSource = mesh;
    group.add(clone);
    meshesToDispose.push(clone);
  }

  orangeMeshes.forEach((mesh, index) => {
    const clone = duplicateFlowMesh(mesh, pitch * 3);
    clone.userData.flowSource = orangePlaced[index];
    group.add(clone);
    meshesToDispose.push(clone);
  });
}

function syncFlowCloneOpacity(group) {
  group.traverse((obj) => {
    const source = obj.userData.flowSource;
    if (!source || source === obj || !obj.isMesh) return;

    obj.material.opacity = source.material.opacity;
  });
}

function getPlaneSizeFromTexture(texture, layout) {
  const image = texture.image;
  if (!image?.width || !image?.height) {
    return { width: layout.w, height: layout.h };
  }

  const imageAspect = image.width / image.height;
  const boxAspect = layout.w / layout.h;

  if (imageAspect > boxAspect) {
    return { width: layout.w, height: layout.w / imageAspect };
  }

  return { width: layout.h * imageAspect, height: layout.h };
}

/** object-fit: cover 相当の平面サイズ */
function getPlaneSizeCover(texture, layout) {
  const image = texture.image;
  if (!image?.width || !image?.height) {
    return { width: layout.w, height: layout.h };
  }

  const imageAspect = image.width / image.height;
  const boxAspect = layout.w / layout.h;

  if (imageAspect > boxAspect) {
    return { width: layout.h * imageAspect, height: layout.h };
  }

  return { width: layout.w, height: layout.w / imageAspect };
}

function createPlane(texture, layout, z) {
  const { width, height } = getPlaneSizeFromTexture(texture, layout);
  const geo = new THREE.PlaneGeometry(width, height);
  const mat = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    depthTest: true,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(layout.x + layout.w / 2, -(layout.y + layout.h / 2), z);
  // 右→左フローで画面端に一部だけ残っても中心が視錐外だと丸ごと消えるため無効化
  mesh.frustumCulled = false;
  return mesh;
}

function createCoverPlane(texture, layout, z) {
  const { width, height } = getPlaneSizeCover(texture, layout);
  const geo = new THREE.PlaneGeometry(width, height);
  const mat = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    depthTest: true,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(layout.x + layout.w / 2, -(layout.y + layout.h / 2), z);
  mesh.frustumCulled = false;
  return mesh;
}

function createFrontPlane(texture, layout) {
  const front = createPlane(texture, layout, Z_FRONT);
  front.renderOrder = 10;
  front.userData = { baseOpacity: 1 };
  return front;
}

function createBackPlane(texture, layout) {
  const blurTexture = createBlurredTexture(texture);
  const back = createPlane(texture, layout, Z_BACK);
  back.material.map = blurTexture;
  back.material.opacity = 0.88;
  back.renderOrder = 0;
  back.userData = {
    baseOpacity: 0.88,
    blurTexture: blurTexture !== texture ? blurTexture : null,
  };
  return back;
}

/** 01 = 前面（sharp） / 02 = 背面（blur）— blue / orange の2セット */
function createPairFlowMeshes(tex01, tex02, tex01Next, tex02Next, layout01, layout02) {
  return {
    blue: {
      front: createFrontPlane(tex01, layout01),
      back: createBackPlane(tex02, layout02),
    },
    orange: {
      front: createFrontPlane(tex01Next, layout01),
      back: createBackPlane(tex02Next, layout02),
    },
  };
}

function createCamera(design) {
  const camera = new THREE.OrthographicCamera(
    0,
    design.width,
    0,
    -design.height,
    1,
    1000,
  );
  camera.position.set(0, 0, 500);
  camera.updateProjectionMatrix();
  return camera;
}

function resizeRenderer(renderer, stage, camera, design) {
  const width = stage.clientWidth;
  if (!width) return false;

  const height =
    stage.clientHeight ||
    Math.round(width * (design.height / design.width));

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height, false);

  const viewWidth = (design.height * width) / height;
  const viewOverflow = viewWidth - design.width; // 画面幅が狭い場合はマイナスになり、左右がクロップされる
  const viewOffset = viewOverflow / 2;

  camera.left = -viewOffset;
  camera.right = design.width + viewOffset;
  camera.top = 0;
  camera.bottom = -design.height;
  camera.updateProjectionMatrix();

  // HTMLテキストの位置とスケールをWebGLカメラと完全に同期させる
  const root = stage.closest(".hm-mv");
  const copy = root?.querySelector(".hm-mv__copy");
  const isSp = window.matchMedia(MV_SP_MQ).matches;
  if (copy && design.width === 1492 && !isSp) { // PC版の場合のみ
    const scale = width / (camera.right - camera.left);
    copy.style.setProperty("--hm-mv-copy-ref-w", "1492px");
    copy.style.setProperty("--hm-mv-copy-ref-x", "0px");
    copy.style.position = "absolute";
    copy.style.left = `${(130 - camera.left) * scale}px`;
    copy.style.top = `${360 * scale}px`;
    copy.style.width = `397px`;
    copy.style.height = `auto`;
    copy.style.margin = "0";
    copy.style.transformOrigin = "top left";
    copy.style.transform = `scale(${scale})`;

    // Canvas側の描画サイズ（397x193）と完全一致させるための補正
    const titleImg = copy.querySelector(".hm-mv__title img");
    if (titleImg) {
      titleImg.style.width = "397px";
      titleImg.style.height = "193px";
    }
    const lead = copy.querySelector(".hm-mv__lead");
    if (lead) {
      // 585 (lead.y) - 360 (title.y) - 193 (title.h) = 32px
      lead.style.marginTop = "32px";
    }
  }

  return true;
}

/**
 * @param {{ easing?: Partial<typeof DEFAULT_EASING> }} [options]
 */
export default async function initHmMv(options = {}) {
  const root = document.querySelector(".hm-mv");
  const canvas = document.querySelector(".js-hm-mv-canvas");
  const stage = document.querySelector(".js-hm-mv-stage");
  if (!canvas || !stage) return;

  const easing = resolveEasing(root, options.easing);

  const basePath = canvas.dataset.assetsBase || "/dist/images/home/";
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  const { design, pairs, isSpStatic, isSp } = getMvLayout(root);

  // --- HTMLテキストのアニメーション用準備 ---
  let copyTitle = null;
  let copyLead = null;
  if (!isSpStatic) {
    copyTitle = root?.querySelector(".hm-mv__title");
    copyLead = root?.querySelector(".hm-mv__lead");
    
    const shouldAddShadow = window.location.pathname.includes("index2") || (window.location.pathname.includes("index3") || window.location.pathname === "/" || window.location.pathname.endsWith("/index.php") || window.location.pathname.endsWith("/html/"));

    if (copyTitle) {
      copyTitle.style.opacity = 0;
      if (shouldAddShadow) {
        copyTitle.classList.add("has-white-shadow");
      }
    }
    if (copyLead) {
      copyLead.style.opacity = 0;
      if (shouldAddShadow) {
        copyLead.classList.add("has-white-shadow");
      }
    }
  }

  const scene = new THREE.Scene();
  const camera = createCamera(design);
  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    powerPreference: "high-performance",
  });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.sortObjects = true;

  resizeRenderer(renderer, stage, camera, design);

  const meshesToDispose = [];
  const texturesToDispose = [];
  const pairMeshes = new Map();
  const blueFrontMeshes = [];
  const blueBackMeshes = [];
  const orangeFrontMeshes = [];
  const orangeBackMeshes = [];
  const topBackFlowGroup = new THREE.Group();
  const bottomBackFlowGroup = new THREE.Group();
  const topFrontFlowGroup = new THREE.Group();
  const bottomFrontFlowGroup = new THREE.Group();
  scene.add(topBackFlowGroup);
  scene.add(bottomBackFlowGroup);
  scene.add(topFrontFlowGroup);
  scene.add(bottomFrontFlowGroup);

  try {
    for (const pair of pairs) {
      if (pair.static) {
        const layout = pair.items[0];
        const tex = await loadTexture(basePath, layout.file);
        const mesh = createCoverPlane(tex, layout, 0);

        scene.add(mesh);
        meshesToDispose.push(mesh);
        texturesToDispose.push(tex);
        pairMeshes.set(pair.id, { meshA: mesh, meshB: null, delay: pair.delay, static: true });
        mesh.material.opacity = 0;
        continue;
      }

      const [layout01, layout02] = pair.items;
      const isMain = pair.id === "main";
      const textureLoads = [
        loadTexture(basePath, layout01.file),
        loadTexture(basePath, layout02.file),
      ];

      if (!isMain) {
        textureLoads.push(
          loadTexture(basePath, layout01.fileNext || getNextTextureFile(layout01.file)),
          loadTexture(basePath, layout02.fileNext || getNextTextureFile(layout02.file)),
        );
      }

      const textures = await Promise.all(textureLoads);
      const [tex01, tex02, tex01Next, tex02Next] = textures;

      if (isMain) {
        const mainBlue = createFrontPlane(tex01, layout01);
        const mainOrange = createFrontPlane(tex02, layout01);
        mainBlue.renderOrder = HERO_RENDER_ORDER;
        mainOrange.renderOrder = HERO_RENDER_ORDER;

        blueFrontMeshes.push(mainBlue);
        orangeFrontMeshes.push(mainOrange);
        meshesToDispose.push(mainBlue, mainOrange);
        texturesToDispose.push(tex01, tex02);
        pairMeshes.set(pair.id, { mainBlue, mainOrange, delay: pair.delay });

        mainBlue.material.opacity = 0;
        mainOrange.material.opacity = 1;
        continue;
      }

      const { blue, orange } = createPairFlowMeshes(
        tex01,
        tex02,
        tex01Next,
        tex02Next,
        layout01,
        layout02,
      );

      blueFrontMeshes.push(blue.front);
      blueBackMeshes.push(blue.back);
      orangeFrontMeshes.push(orange.front);
      orangeBackMeshes.push(orange.back);
      meshesToDispose.push(blue.front, blue.back, orange.front, orange.back);
      pairMeshes.set(pair.id, {
        blue,
        orange,
        delay: pair.delay,
      });

      texturesToDispose.push(tex01, tex02, tex01Next, tex02Next);
      if (blue.back.userData.blurTexture) texturesToDispose.push(blue.back.userData.blurTexture);
      if (orange.back.userData.blurTexture) {
        texturesToDispose.push(orange.back.userData.blurTexture);
      }

      blue.front.material.opacity = 0;
      blue.back.material.opacity = 0;
      orange.front.material.opacity = 1;
      orange.back.material.opacity = 0.88;
    }

    if (!isSpStatic && !isSp) {
      const palette = getHmMvColorPalette(root);
      const titleImg = await loadSvgImage(basePath, HERO_COPY.title.file);
      const titleTex = createHeroImageTexture(titleImg, HERO_COPY.title);
      const leadBlueTex = createLeadTexture(
        HERO_COPY.lines,
        palette.blue["--hm-mv-lead-color"],
        HERO_COPY.lead,
      );
      const leadOrangeTex = createLeadTexture(
        HERO_COPY.lines,
        palette.orange["--hm-mv-lead-color"],
        HERO_COPY.lead,
      );

      if (leadBlueTex && leadOrangeTex && titleTex) {
        const titleBlue = createFrontPlane(titleTex, HERO_COPY.title);
        const titleOrange = createFrontPlane(titleTex, HERO_COPY.title);
        const leadBlue = createFrontPlane(leadBlueTex, HERO_COPY.lead);
        const leadOrange = createFrontPlane(leadOrangeTex, HERO_COPY.lead);

        for (const mesh of [titleBlue, titleOrange, leadBlue, leadOrange]) {
          mesh.renderOrder = HERO_RENDER_ORDER + 1;
        }

        blueFrontMeshes.push(titleBlue, leadBlue);
        orangeFrontMeshes.push(titleOrange, leadOrange);
        meshesToDispose.push(titleBlue, titleOrange, leadBlue, leadOrange);
        texturesToDispose.push(titleTex, leadBlueTex, leadOrangeTex);

        titleBlue.material.opacity = 0;
        leadBlue.material.opacity = 0;
        titleOrange.material.opacity = 0;
        leadOrange.material.opacity = 0;

        pairMeshes.set("hero-copy", { titleBlue, titleOrange, leadBlue, leadOrange });
      } else {
        texturesToDispose.push(titleTex);
      }
    }
  } catch (error) {
    console.error("[hm-mv] Failed to load textures:", error);
    showHmMvFallback(root, stage);
    return;
  }

  stage.classList.add("is-ready");

  let colorShift = null;
  let flowSegment = "blue";
  let flowController = null;

  if (!isSpStatic) {
    const isTop = (m) => m.position.y > -design.height / 2;
    const isBottom = (m) => m.position.y <= -design.height / 2;

    populateAlternatingFlowGroup(topBackFlowGroup, blueBackMeshes.filter(isTop), orangeBackMeshes.filter(isTop), design.width, meshesToDispose);
    populateAlternatingFlowGroup(bottomBackFlowGroup, blueBackMeshes.filter(isBottom), orangeBackMeshes.filter(isBottom), design.width, meshesToDispose);
    populateAlternatingFlowGroup(topFrontFlowGroup, blueFrontMeshes.filter(isTop), orangeFrontMeshes.filter(isTop), design.width, meshesToDispose);
    populateAlternatingFlowGroup(bottomFrontFlowGroup, blueFrontMeshes.filter(isBottom), orangeFrontMeshes.filter(isBottom), design.width, meshesToDispose);

    if (!prefersReducedMotion) {
      const isIndex3 = (window.location.pathname.includes("index3") || window.location.pathname === "/" || window.location.pathname.endsWith("/index.php") || window.location.pathname.endsWith("/html/"));
      const isIndex4 = window.location.pathname.includes("index4");
      const isStopAtSpot = isIndex3 || isIndex4;
      flowController = createFlowController([
        { group: topBackFlowGroup, duration: FLOW_TOP_BACK_DURATION },
        { group: bottomBackFlowGroup, duration: isStopAtSpot ? FLOW_BOTTOM_FRONT_DURATION : FLOW_BOTTOM_BACK_DURATION },
        { group: topFrontFlowGroup, duration: FLOW_TOP_FRONT_DURATION },
        { group: bottomFrontFlowGroup, duration: FLOW_BOTTOM_FRONT_DURATION },
      ]);
      updateFlowController(flowController, design.width, undefined);

      if (root) {
        colorShift = createHmMvColorShift(root, easing);
        colorShift.resetBlue();
      }
    }
  }

  // prefers-reduced-motion: いきなり表示してフローもしない
  if (prefersReducedMotion) {
    root?.classList.add("is-bg-in", "is-visual-in", "is-overlay-in");
    for (const [id, pairEntry] of pairMeshes) {
      const { meshA, meshB, blue, static: isStatic } = pairEntry;
      if (isStatic) {
        meshA.material.opacity = 1;
        continue;
      }

      if (id === "main") {
        pairEntry.mainBlue.material.opacity = 1;
        pairEntry.mainOrange.material.opacity = 0;
      } else if (id === "hero-copy") {
        pairEntry.titleBlue.material.opacity = 1;
        pairEntry.leadBlue.material.opacity = 1;
        pairEntry.titleOrange.material.opacity = 0;
        pairEntry.leadOrange.material.opacity = 0;
      } else {
        blue.front.material.opacity = 1;
        blue.back.material.opacity = 0.88;
      }
    }
    // HTMLテキストの初期化（即時表示）
    if (copyTitle) copyTitle.style.opacity = 1;
    if (copyLead) copyLead.style.opacity = 1;
  } else if (isSpStatic) {
    const spMesh = pairMeshes.get("sp-static")?.meshA;
    const intro = gsap.timeline();

    intro.call(() => root?.classList.add("is-bg-in"), undefined, 0);
    intro.call(() => root?.classList.add("is-visual-in"), undefined, 0.05);
    intro.to(
      spMesh?.material || {},
      { opacity: 1, duration: 0.6, ease: easing.introMain },
      0.2,
    );
    intro.call(() => root?.classList.add("is-overlay-in"), undefined, 0.9);
  } else {
    // 初回表示シーケンス
    const intro = gsap.timeline();

    intro.call(() => root?.classList.add("is-bg-in"), undefined, 0);
    intro.call(() => root?.classList.add("is-visual-in"), undefined, 0.05);
    intro.call(() => root?.classList.add("is-overlay-in"), undefined, 0.05);

    const mainEntry = pairMeshes.get("main");
    const introHeroTargets = [
      mainEntry?.mainBlue?.material,
    ].filter(Boolean);

    // 1) 背景ペアを順にフェードイン（-nextなし群 = blue）
    const FIRST_PHOTO_START = 0.2;
    const introOrder = ["left-top", "center-top", "right-top", "right-bottom"];
    introOrder.forEach((id, idx) => {
      const pair = pairMeshes.get(id);
      if (!pair) return;

      const time = FIRST_PHOTO_START + idx * 0.4;
      intro.to(pair.blue.front.material, { opacity: 1, duration: INTRO_PAIR_FADE_DURATION, ease: easing.introPair }, time);
      intro.to(pair.blue.back.material, { opacity: 0.88, duration: INTRO_PAIR_FADE_DURATION, ease: easing.introPair }, time);
    });

    // 2) タイトル SVG + テキストアニメーション開始
    // 最後の写真が現れ始めるのが 0.2 + 3 * 0.4 = 1.4秒、表示完了が2.6秒
    const TITLE_START = 1.8;
    
    // HTMLタイトル フェードイン
    if (copyTitle) {
      intro.to(copyTitle, { opacity: 1, duration: 1.2, ease: easing.introMain }, TITLE_START);
    }
    // HTMLテキスト フェードイン
    if (copyLead) {
      intro.to(copyLead, { opacity: 1, duration: 1.2, ease: easing.introMain }, TITLE_START);
    }

    // 3) main01 フェードイン
    const MAIN01_START = TITLE_START + 1.5;
    intro.to(introHeroTargets, { opacity: 1, duration: 1.2, ease: easing.introMain }, MAIN01_START);

    // アニメーション開始直前にHTMLとWebGLをスワップする
    intro.addLabel("swap");
    const heroCopy = pairMeshes.get("hero-copy");
    const isIndex2 = window.location.pathname.includes("index2");
    const isIndex3 = (window.location.pathname.includes("index3") || window.location.pathname === "/" || window.location.pathname.endsWith("/index.php") || window.location.pathname.endsWith("/html/"));

    if (heroCopy) {
      if (!isIndex2 && !isIndex3) {
        if (copyTitle) intro.set(copyTitle, { visibility: "hidden" }, "swap");
        if (copyLead) intro.set(copyLead, { visibility: "hidden" }, "swap");
        intro.set(
          [
            heroCopy.titleBlue.material,
            heroCopy.leadBlue.material,
            heroCopy.titleOrange.material,
            heroCopy.leadOrange.material,
          ],
          { opacity: 1 },
          "swap",
        );
      }
    }

    // アニメーション開始（intro 完了後）
    intro.call(() => {
      if (flowController) {
        startFlowController(flowController);
      }
    }, undefined, "swap" + MAIN_LOOP_DELAY);
  }

  const render = () => {
    if (!prefersReducedMotion && !isSpStatic) {
      if (flowController) {
        updateFlowController(flowController, design.width, colorShift);
      }

      syncFlowCloneOpacity(topFrontFlowGroup);
      syncFlowCloneOpacity(bottomFrontFlowGroup);
      syncFlowCloneOpacity(topBackFlowGroup);
      syncFlowCloneOpacity(bottomBackFlowGroup);

      const isIndex3 = (window.location.pathname.includes("index3") || window.location.pathname === "/" || window.location.pathname.endsWith("/index.php") || window.location.pathname.endsWith("/html/"));
      const isIndex4 = window.location.pathname.includes("index4");
      if (!isIndex3 && !isIndex4) {
        const segment = getFlowSegment(
          topFrontFlowGroup,
          design.width,
          design.height,
          flowSegment,
        );
        if (segment !== flowSegment) {
          flowSegment = segment;
          if (segment === "orange") {
            colorShift?.toOrange();
          } else {
            colorShift?.toBlue();
          }
        }
      }
    }
    renderer.render(scene, camera);
  };

  const resize = () => {
    const { design: currentDesign } = getMvLayout(root);
    if (resizeRenderer(renderer, stage, camera, currentDesign)) {
      render();
    }
  };

  resize();
  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(stage);

  render();
  gsap.ticker.add(render);

  // --- スクロール連動アニメーション (PC版のみ) ---
  const mm = gsap.matchMedia();
  mm.add("(min-width: 1025px)", () => {
    const bg = root?.querySelector(".hm-mv__bg");
    const copy = root?.querySelector(".hm-mv__copy");
    const polys = root?.querySelectorAll(".hm-mv__poly") || [];

    const st = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: "top top",
        end: "bottom top",
        scrub: true,
      },
    });

    // キャンバス（画像群）は遅れて動く（パララックス）
    if (canvas) {
      st.to(canvas, { y: 200, ease: "none" }, 0);
    }
    // 背景SVG全体
    if (bg) {
      st.to(bg, { y: 100, ease: "none" }, 0);
    }
    // 個別のポリゴンに視差効果を追加
    polys.forEach((poly, i) => {
      const speed = i % 3 === 0 ? -50 : i % 2 === 0 ? 50 : 150;
      st.to(poly, { y: speed, ease: "none" }, 0);
    });
    // テキストはWebGL側に描画されるため個別アニメーション不要
  });

  return () => {
    mm.revert();
    gsap.ticker.remove(render);
    resizeObserver.disconnect();
    meshesToDispose.forEach((mesh) => {
      mesh.geometry.dispose();
      mesh.material.dispose();
    });
    texturesToDispose.forEach((texture) => texture.dispose());
    renderer.dispose();
  };
}
